import React from 'react';
import { Icon } from '@iconify/react';
import { formatIntDateTime } from '../utils/dateUtils';

const WorkOrderDataTable = ({
  workOrders,
  onView,
  onEdit,
  onDelete,
  onAddToEvent,
  onCloneWorkOrder,
  onExportPdf,
  canUpdateWorkOrder,
  loading,
  sortField,
  sortOrder,
  onSort,
}) => {
  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field, iconOptions = {}) => {
    const ariaSort = sortField === field
      ? sortOrder === 'asc'
        ? 'ascending'
        : sortOrder === 'desc'
          ? 'descending'
          : 'none'
      : 'none';

    const iconClassName = iconOptions.className
      ? `text-xl ${iconOptions.className}`
      : 'text-xl';

    return (
      <th
        onClick={() => onSort?.(field)}
        style={{ cursor: 'pointer' }}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon
            className={iconClassName}
            style={iconOptions.style}
            icon={getSortIcon(field)}
          />
        </span>
      </th>
    );
  };

  return (
    <table className="table bordered-table mb-3 rounded-0 w-100">
      <thead>
        <tr>
          <th className="rounded-0"></th>
          {renderSortableHeader('Number', 'number')}
          {renderSortableHeader('Title', 'title')}
          {renderSortableHeader('Status', 'status')}
          {renderSortableHeader('Priority', 'priority')}
          {renderSortableHeader('Task Type', 'taskType')}
          {renderSortableHeader('Asset or Area', 'assetOrArea')}
          <th>Team</th>
          {renderSortableHeader('Scheduled At', 'scheduledAt', {
            style: { position: 'relative', top: '-4px' },
            className: 'ms-6',
          })}
          <th className="rounded-0">Actions</th>
        </tr>
      </thead>
      <tbody>
        {workOrders.length === 0 && (
          <tr>
            <td colSpan="10" className="text-center py-4 rounded-0">No Work Orders</td>
          </tr>
        )}
        {workOrders.map((row) => {
          const isAdvancedAsset = Number(row.asset?.PlanningTypeId) === 1;
          const shouldShowAddToEvent =
            isAdvancedAsset && !(row.statusId === 4 && row.eventId === null);
          const isOverdue =
            row.dueDate && new Date(row.dueDate) < new Date() && ![4, 5, 6].includes(row.statusId);
          const hasParts = row.partsCount > 0;
          const isRecurring = row.workOrderScheduleId || row.taskId;
          const priorityColour = row.priority?.colours?.bootstrapName || 'secondary';
          const statusColour = row.status?.colours?.bootstrapName || 'secondary';
          const teamInitials = row.team?.teamName
            ? row.team.teamName
              .split(' ')
              .slice(0, 2)
              .map((w) => w[0])
              .join('')
              .toUpperCase()
            : '';
          return (
            <tr key={row.id} >
              <td>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {row.statutory === 1 && (
                    <Icon
                      icon="mdi:alert-rhombus-outline"
                      className="text-danger text-xxl me-1"
                      style={{ position: 'relative', top: '-1px' }}
                    />
                  )}
                  {isRecurring && (
                    <Icon
                      icon="mdi:backup-restore"
                      className="text-primary text-xxl me-1"
                      style={{ position: 'relative', top: '-1px' }}
                    />
                  )}
                  {hasParts && (
                    <Icon
                      icon="mage:settings"
                      className="text-secondary text-xxl me-1"
                      style={{ position: 'relative', top: '-1px' }}
                    />
                  )}
                </span>
              </td>
              <td>{row.workOrderNumber}</td>
              <td>
                <strong>{row.workOrderTitle || ''}</strong>
              </td>
              <td>
                {row.status && (
                  <span className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill fw-medium text-sm`}>
                    {row.status.name}
                  </span>
                )}
              </td>
              <td>
                {row.priority && (
                  <div className="d-flex align-items-center gap-2">
                    <span className={`d-inline-block rounded-1 bg-${priorityColour}`} style={{ width: '10px', height: '10px' }}></span>
                    {row.priority.name}
                  </div>
                )}
              </td>
              <td>{row.taskType?.taskTypeCode || ''}</td>
              <td>
                {row.assetId
                  ? `${row.asset?.assetNumber || ''} - ${row.asset?.assetDescription || ''}`
                  : row.areaId
                    ? `${row.area?.code || ''} - ${row.area?.name || ''}`
                    : ''}
              </td>
              <td>
                {row.team ? (
                  <div
                    className={`rounded-circle bg-${row.team.colours?.bootstrapName} d-flex align-items-center justify-content-center text-white fw-bold`}
                    style={{ width: '35px', height: '35px', fontSize: '12px' }}
                  >
                    {teamInitials}
                  </div>
                ) : (
                  '-'
                )}
              </td>
              <td style={{ width: "160px" }}>
                {formatIntDateTime(row.scheduledAt)}
                {isOverdue && (
                  <Icon
                    icon="mdi:exclamation-thick"
                    className="text-danger text-lg ms-2"
                    style={{ position: 'relative', top: '-5px' }}
                  />
                )}
              </td>
              <td>
                {!canUpdateWorkOrder && (
                  <button
                    className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    onClick={() => onView(row)}
                  >
                    <Icon icon="ic:baseline-visibility" />
                  </button>
                )}
                {canUpdateWorkOrder && (<button
                  className={`edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center ${canUpdateWorkOrder ? '' : 'ms-6'}`}
                  onClick={() => onEdit(row)}
                >
                  <Icon icon="ic:baseline-edit" />
                </button>)}
                <button
                  className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ms-6"
                  onClick={() => onDelete(row.id)}
                >
                  <Icon icon="ic:baseline-delete-outline" />
                </button>
                <div className="dropdown d-inline-block ms-6">
                  <button
                    type="button"
                    className="btn w-32-px h-32-px p-0 border-0 text-secondary d-inline-flex align-items-center justify-content-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <Icon icon="mdi:dots-vertical" className="text-xl" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    {shouldShowAddToEvent && (
                      <li>
                        <button type="button" className="dropdown-item py-12" onClick={() => onAddToEvent?.(row)}>
                          + Add To Event
                        </button>
                      </li>
                    )}
                    <li>
                      <button type="button" className="dropdown-item py-12" onClick={() => onCloneWorkOrder?.(row)}>
                        Clone Work Order
                      </button>
                    </li>
                    <li>
                      <button type="button" className="dropdown-item py-12" onClick={() => onExportPdf?.(row)}>
                        Export to PDF
                      </button>
                    </li>
                    <li>
                      <button type="button" className="dropdown-item py-12 text-danger" onClick={() => onDelete(row.id)}>
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          );
        })}
        {loading && (
          <tr>
            <td colSpan={10} className="text-center py-2">
              <span className="spinner-border spinner-border-sm" />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default WorkOrderDataTable;
